import '@fortawesome/fontawesome-free/css/all.min.css'
import "react-datetime/css/react-datetime.css";
import './styles/main.css'

import * as React from 'react'
import { createRoot } from 'react-dom/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import { QueryClient, QueryClientProvider } from 'react-query';
import { AppService } from 'web/AppService';
import { App } from './App';
import { User } from './model/User';
import { fas } from '@fortawesome/free-solid-svg-icons'
import { Theme } from 'react-daisyui'
import * as Sentry from "@sentry/react";
import { BrowserRouter, createRoutesFromChildren, matchRoutes, Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import TagManager from 'react-gtm-module';
import Cookies from 'js-cookie';
import { configureAnalyticsTelemetry, trackAnalyticsEvent } from './utils/analyticsTelemetry';

const BASE_URL = ''

library.add(fas)

const container = document.getElementById('root')
const root = createRoot(container!)

const queryClient = new QueryClient({
    defaultOptions: {
    },
})

// Public NxHUB onboarding QR landing page (/onboard/:deviceId). This must render
// for a logged-OUT visitor, so short-circuit BEFORE the authed bootstrap below
// (which fetches /api/token-auth/me and dereferences INIT_USER). The whole app
// only mounts once that promise resolves with a user, so a plain <Route> is not
// enough — we mount the page directly here.
if (window.location.pathname.startsWith('/onboard/')) {
    // Set the mobile viewport SYNCHRONOUSLY, before the lazy page chunk loads or
    // React paints. The page's useEffect also sets it (and handles restore on
    // in-app navigation), but for a direct QR-scan entry that effect runs only
    // after the lazy import resolves — long enough for mobile Safari to commit
    // to its ~980px default and flash a scaled-down layout. index.html has no
    // viewport tag (so the desktop portal in the else-branch is unaffected), so
    // we add one here for this onboard-only entry path.
    if (!document.querySelector('meta[name="viewport"]')) {
        const vp = document.createElement('meta');
        vp.name = 'viewport';
        vp.content = 'width=device-width, initial-scale=1';
        document.head.appendChild(vp);
    }

    const OnboardNxHubPage = React.lazy(() =>
        import('web/pages/onboard/OnboardNxHubPage').then(m => ({ default: m.OnboardNxHubPage })));
    root.render(
        <Theme dataTheme="light">
            <BrowserRouter>
                <React.Suspense fallback={null}>
                    <Routes>
                        <Route path="/onboard/:deviceId" element={<OnboardNxHubPage />} />
                    </Routes>
                </React.Suspense>
            </BrowserRouter>
        </Theme>
    );
} else {

const preRenderPromise = (window as any).INIT_USER ?
    Promise.resolve((window as any).INIT_USER) :
    fetch('/api/token-auth/me')
        .then(response => response.ok ? response.json() : ({}))
        .catch(() => ({}));

preRenderPromise.then(data => {
    (window as any).INIT_USER = data
    const organizationId = Cookies.get('nx-org');

    TagManager.initialize({
        gtmId: 'GTM-PRSBPGWD',
        dataLayer: {
            userId: (window as any).INIT_USER.id,
            userEmail: (window as any).INIT_USER.email,
            organizationId,
            sendPageView: false,
        },
    });

    configureAnalyticsTelemetry({ organizationId, userId: (window as any).INIT_USER?.id });
    trackAnalyticsEvent('app_loaded');

    const initialUser = new User((window as any).INIT_USER)
    root.render(
        <QueryClientProvider client={queryClient}>
            <Theme dataTheme="light">
                <AppService baseUrl = {BASE_URL} initialUser={initialUser}
                    renderChildren={(appContext) => <App context={appContext} /> }/>
            </Theme>
        </QueryClientProvider>
    )
});

}

Sentry.init({
    dsn: "https://27ca6d436bd3814a3b76477b4c005390@o4507968713064448.ingest.us.sentry.io/4507969229029376",
    integrations: [
        Sentry.browserTracingIntegration(),
        // Sentry.replayIntegration(),  //TODO: Replay eats too much memory. Research a way to enable it only for specific users.
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
    ],

    beforeSend(event) {
        const isLocalhost = window.location.hostname === 'localhost';
        return isLocalhost ? null : event;
    },

    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/app.nx\.vet\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});
